import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CapacityScheduleEditor from "./CapacityScheduleEditor";
import CapacityBulkUpload from "./CapacityBulkUpload";
import {
  getCapacitySchedule,
  replaceCapacitySchedule,
  updateLocation,
} from "../requests/location-requests";
import type { LocationCapacityScheduleRow } from "../../../types/LocationCapacitySchedule";
import { getErrorMessage } from "../../../util/NetworkErrorUtil";

interface CapacityManagerDialogProps {
  open: boolean;
  locationId: string;
  locationName: string;
  /**
   * Location's default capacity (totalNumberOfSpaces). Editable in the header
   * box and used as the fallback in the calendar preview for days without a
   * scheduled capacity.
   */
  defaultCapacity: number | null;
  /** Number of auto-approve spaces (managedSpaces). */
  autoApproveSpaces: number | null;
  /**
   * Whether automated approvals ("Allow automated approvals of space
   * reservations") are enabled for this location. When true, the auto-approve
   * spaces field is shown and editable in the header box.
   */
  managedLocation: boolean;
  onClose: () => void;
  /** Called after a successful save/upload so the parent can refresh cards. */
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}

/**
 * POC: consolidates both supplier capacity-entry methods behind a single
 * "Capacity" action on the card — a schedule/calendar editor (option c) and a
 * bulk CSV/Excel upload (option d) — so the card header stays uncluttered.
 *
 * Presented as a full-screen view styled to match the create/edit location
 * wizard (grey page, centered elevated Paper card, bordered header, and a
 * grey footer action bar). It is not a wizard — there are no steps — it just
 * shares the same look and feel now that the feature is approved.
 */
const CapacityManagerDialog = ({
  open,
  locationId,
  locationName,
  defaultCapacity,
  autoApproveSpaces,
  managedLocation,
  onClose,
  onSaved,
  onError,
}: CapacityManagerDialogProps) => {
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState<LocationCapacityScheduleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Editable header-box state. `liveDefaultCapacity` mirrors the location's
  // default capacity so the calendar preview updates immediately after an inline
  // save, without waiting for the parent to refetch.
  const [liveDefaultCapacity, setLiveDefaultCapacity] = useState<number | null>(defaultCapacity);
  const [capacityInput, setCapacityInput] = useState("");
  const [autoApproveInput, setAutoApproveInput] = useState("");
  const [savingCapacity, setSavingCapacity] = useState(false);
  const [capacityError, setCapacityError] = useState<string | null>(null);
  const [autoApproveError, setAutoApproveError] = useState<string | null>(null);

  // Keep the latest onError/onClose without making them effect dependencies, so a
  // parent re-render (new inline callback identity) can't retrigger the effects.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Tie the full-screen dialog to browser history so the Back button closes it
  // and returns to the locations page instead of navigating away. On open we push
  // a duplicate history entry (preserving react-router's state so its history
  // index stays in sync); Back then pops that entry and closes the dialog. When
  // the dialog is closed via the UI instead, we pop our own entry so a later Back
  // press isn't swallowed by a stale entry.
  useEffect(() => {
    if (!open) return;
    let poppedByBack = false;
    window.history.pushState(window.history.state, "");

    const handlePopState = () => {
      poppedByBack = true;
      onCloseRef.current();
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      if (!poppedByBack) {
        window.history.back();
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let ignore = false;
    setLoading(true);
    setSavedAt(null);
    getCapacitySchedule(locationId)
      .then((response) => {
        if (!ignore) setRows(response.body as LocationCapacityScheduleRow[]);
      })
      .catch((err) => {
        if (!ignore) onErrorRef.current(getErrorMessage(err) || "Failed to load capacity schedule");
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [open, locationId]);

  // Seed the editable header fields whenever the dialog (re)opens or the
  // location's stored values change.
  useEffect(() => {
    if (!open) return;
    setLiveDefaultCapacity(defaultCapacity);
    setCapacityInput(defaultCapacity != null ? String(defaultCapacity) : "");
    setAutoApproveInput(autoApproveSpaces != null ? String(autoApproveSpaces) : "");
    setCapacityError(null);
    setAutoApproveError(null);
  }, [open, defaultCapacity, autoApproveSpaces]);

  const capacityChanged = capacityInput.trim() !== String(liveDefaultCapacity ?? "");
  const autoApproveChanged =
    managedLocation && autoApproveInput.trim() !== String(autoApproveSpaces ?? "");
  const hasCapacityChanges = capacityChanged || autoApproveChanged;

  /**
   * PATCH only the capacity values that actually changed. Each field is validated
   * as a whole number ≥ 1; nothing is sent when the inputs are unchanged.
   */
  const handleSaveCapacities = async () => {
    const payload: { totalNumberOfSpaces?: number; managedSpaces?: number } = {};
    let hasError = false;

    if (capacityChanged) {
      const trimmed = capacityInput.trim();
      const value = Number(trimmed);
      if (trimmed === "" || !Number.isInteger(value) || value < 1) {
        setCapacityError("Enter a whole number of at least 1");
        hasError = true;
      } else {
        setCapacityError(null);
        payload.totalNumberOfSpaces = value;
      }
    }

    if (autoApproveChanged) {
      const trimmed = autoApproveInput.trim();
      const value = Number(trimmed);
      if (trimmed === "" || !Number.isInteger(value) || value < 1) {
        setAutoApproveError("Enter a whole number of at least 1");
        hasError = true;
      } else {
        setAutoApproveError(null);
        payload.managedSpaces = value;
      }
    }

    // Cross-field validation: auto-approve spaces cannot exceed total capacity.
    const nextTotal = payload.totalNumberOfSpaces ?? liveDefaultCapacity;
    const nextManaged = payload.managedSpaces ?? autoApproveSpaces;
    if (
      managedLocation &&
      nextTotal != null &&
      nextManaged != null &&
      nextManaged > nextTotal
    ) {
      setAutoApproveError("Auto-approve spaces cannot exceed your location capacity.");
      return;
    }

    if (hasError || Object.keys(payload).length === 0) return;

    setSavingCapacity(true);
    try {
      await updateLocation(locationId, payload);
      if (payload.totalNumberOfSpaces != null) {
        setLiveDefaultCapacity(payload.totalNumberOfSpaces);
      }
      onSaved("Location capacity updated");
    } catch (err) {
      onErrorRef.current(getErrorMessage(err) || "Failed to update capacity");
    } finally {
      setSavingCapacity(false);
    }
  };

  const handleSaveSchedule = async () => {
    // Drop incomplete rows so a half-filled row can't 400 the whole save.
    const validRows = rows.filter(
      (row) => row.effectiveDate && Number.isFinite(row.capacity),
    );
    setSaving(true);
    try {
      const payload = validRows.map((row) => ({
        effectiveDate: row.effectiveDate,
        endDate: row.endDate || null,
        capacity: row.capacity,
        note: row.note || null,
      }));
      const response = await replaceCapacitySchedule(locationId, payload);
      // Replace local rows with the persisted rows (now carrying server ids) so
      // the dialog stays open and the supplier can keep adding more schedules.
      setRows(response.body as LocationCapacityScheduleRow[]);
      setSavedAt(new Date());
      onSaved("Capacity schedule saved");
    } catch (err) {
      onError(getErrorMessage(err) || "Failed to save capacity schedule");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Box sx={{ bgcolor: "grey.50", minHeight: "100vh", py: 4 }}>
        <Container maxWidth={false}>
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden", maxWidth: 1040, mx: "auto" }}>
            <Box
              sx={{
                px: { xs: 2, sm: 4 },
                pt: 4,
                pb: 3,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography variant="h5" sx={{ mb: 0.5 }}>
                  Manage Capacity
                </Typography>
                <Typography variant="body2" sx={{ color: "#5E7782" }}>
                  {locationName}
                </Typography>
              </Box>

              {/* Polished default capacity + auto-approve card */}
              <Paper
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 3,
                  overflow: "hidden",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2.5,
                    py: 1.5,
                    bgcolor: "primary.main",
                    color: "#fff",
                  }}
                >
                  <WarehouseOutlinedIcon fontSize="small" />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      Capacity
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85 }}>
                      Location defaults
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ p: 2.5 }}>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <TextField
                      label="Default Capacity"
                      type="number"
                      inputProps={{ min: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <WarehouseOutlinedIcon fontSize="small" sx={{ color: "text.disabled" }} />
                          </InputAdornment>
                        ),
                      }}
                      value={capacityInput}
                      onChange={(e) => {
                        setCapacityInput(e.target.value);
                        if (capacityError) setCapacityError(null);
                      }}
                      error={Boolean(capacityError)}
                      helperText={capacityError || "Fallback for unscheduled days."}
                      sx={{ width: { xs: "100%", sm: 210 } }}
                    />

                    {managedLocation && (
                      <TextField
                        label="Auto-Approve Spaces"
                        type="number"
                        inputProps={{ min: 1 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BoltOutlinedIcon fontSize="small" sx={{ color: "text.disabled" }} />
                            </InputAdornment>
                          ),
                        }}
                        value={autoApproveInput}
                        onChange={(e) => {
                          setAutoApproveInput(e.target.value);
                          if (autoApproveError) setAutoApproveError(null);
                        }}
                        error={Boolean(autoApproveError)}
                        helperText={autoApproveError || "Auto-approved without review."}
                        sx={{ width: { xs: "100%", sm: 210 } }}
                      />
                    )}
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={handleSaveCapacities}
                      disabled={savingCapacity || !hasCapacityChanges}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      {savingCapacity ? "Saving…" : "Save changes"}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Box>

            <Box sx={{ px: { xs: 2, sm: 4 }, py: 4 }}>
              <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
                <Tab label="Schedule" />
                <Tab label="Bulk Upload" />
              </Tabs>

              {tab === 0 && (
                <Box>
                  {loading ? (
                    <Typography variant="body2" sx={{ color: "#8299A5" }}>
                      Loading…
                    </Typography>
                  ) : (
                    <>
                      {savedAt && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          Saved. You can keep adding capacity changes and save again.
                        </Alert>
                      )}
                      <CapacityScheduleEditor
                        rows={rows}
                        onChange={(next) => {
                          setSavedAt(null);
                          setRows(next);
                        }}
                        fallbackCapacity={liveDefaultCapacity}
                      />
                    </>
                  )}
                </Box>
              )}

              {tab === 1 && (
                <CapacityBulkUpload
                  locationId={locationId}
                  onUploaded={(result) => {
                    setRows(result.schedule);
                    onSaved(
                      `Uploaded capacity — ${result.rowsAccepted} row(s) accepted`,
                    );
                  }}
                />
              )}
            </Box>

            <Box
              component="footer"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: { xs: 2, sm: 4 },
                py: 2,
                borderTop: 1,
                borderColor: "divider",
                bgcolor: "grey.50",
              }}
            >
              <Button variant="text" color="inherit" onClick={onClose}>
                Cancel
              </Button>
              <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
                {tab === 0 && (
                  <Button variant="contained" onClick={handleSaveSchedule} disabled={saving || loading}>
                    {saving ? "Saving…" : "Save Schedule"}
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Dialog>
  );
};

export default CapacityManagerDialog;
